import { useCallback, useEffect, useRef, useState } from 'react';
import { OTP_RESEND_COUNTDOWN_SECONDS } from '../lib/otp';

/**
 * Cooldown de reenvío de OTP refresh-proof (DEC-RBAC-056).
 *
 * Guarda el timestamp de envío (`sentAt`, epoch ms) en `sessionStorage` bajo una clave
 * namespaced por `flowKey` y computa el restante como `cooldown − (now − sentAt)`. Como la
 * fuente de verdad es el timestamp (no un contador en memoria), el countdown SOBREVIVE un
 * refresh: al re-montar, el hook lee el `sentAt` previo y continúa la cuenta donde iba.
 *
 * Reemplaza los `useState`/`setInterval` de countdown de cada pantalla de OTP (login,
 * forgot-password, self-register). Cada flujo usa su propia `flowKey` → cooldowns aislados.
 *
 * @param flowKey  identificador del flujo (ej. 'login', 'forgot-password', 'self-register').
 *                 Namespacea la clave de storage para que un flujo no pise el cooldown de otro.
 * @param cooldownSeconds  duración del cooldown (default = TTL del OTP backend, 2 min).
 */
export interface OtpCooldown {
    /** Segundos restantes hasta poder reenviar (0 si ya se puede). */
    secondsLeft: number;
    /** `true` cuando el cooldown terminó (o nunca arrancó). */
    canResend: boolean;
    /** Marca "acabo de enviar un OTP ahora" → reinicia el cooldown (persistido). */
    markSent: () => void;
    /** Olvida el cooldown (limpia el storage). Para "cancelar"/reset del flujo. */
    clear: () => void;
}

const STORAGE_PREFIX = 'fiado.otpCooldown.';

/** flowKey del cooldown del login — compartido por el hook y por la limpieza del store de login. */
export const LOGIN_OTP_FLOW_KEY = 'login';

function storageKey(flowKey: string): string {
    return `${STORAGE_PREFIX}${flowKey}`;
}

/**
 * Borra el cooldown persistido de un flujo (fuera de un componente). Lo usa el `reset()` del store
 * de login para limpiar el cooldown junto con el resto del flujo, sin que el store tenga que
 * conocer la convención de keys del cooldown (la dueña sigue siendo este módulo). Best-effort.
 */
export function clearOtpCooldown(flowKey: string): void {
    try {
        sessionStorage.removeItem(storageKey(flowKey));
    } catch {
        /* best-effort: sessionStorage inaccesible */
    }
}

function readSentAt(flowKey: string): number | null {
    try {
        const raw = sessionStorage.getItem(storageKey(flowKey));
        if (!raw) return null;
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
    } catch {
        // sessionStorage inaccesible (modo privado viejo, etc.): degradamos a "sin cooldown".
        return null;
    }
}

function computeSecondsLeft(sentAt: number | null, cooldownSeconds: number, now: number): number {
    if (sentAt === null) return 0;
    const elapsedMs = now - sentAt;
    const remainingSec = Math.ceil((cooldownSeconds * 1000 - elapsedMs) / 1000);
    return remainingSec > 0 ? remainingSec : 0;
}

export function useOtpCooldown(
    flowKey: string,
    cooldownSeconds: number = OTP_RESEND_COUNTDOWN_SECONDS,
): OtpCooldown {
    // `secondsLeft` se mantiene en estado y solo se actualiza desde callbacks (interval / markSent
    // / clear) — nunca con setState síncrono dentro del cuerpo de un effect, ni leyendo el reloj
    // durante el render (ambos prohibidos por las reglas de pureza de React). La fuente de verdad
    // del cooldown es el `sentAt` PERSISTIDO en sessionStorage: el estado es solo una proyección
    // que el interval refresca. Refresh-proof: al montar leemos el `sentAt` previo del storage.
    const [secondsLeft, setSecondsLeft] = useState<number>(() =>
        computeSecondsLeft(readSentAt(flowKey), cooldownSeconds, Date.now()),
    );

    // Resincroniza el cooldown desde el storage cuando cambia el flujo. Va en un effect (no en el
    // cuerpo del render: leer el reloj durante el render es impuro). Setea dentro de un microtask
    // para no romper la regla de "no setState síncrono en el cuerpo del effect".
    const prevFlowKey = useRef(flowKey);
    useEffect(() => {
        if (prevFlowKey.current === flowKey) return;
        prevFlowKey.current = flowKey;
        queueMicrotask(() => {
            setSecondsLeft(computeSecondsLeft(readSentAt(flowKey), cooldownSeconds, Date.now()));
        });
    }, [flowKey, cooldownSeconds]);

    // Tick cada segundo mientras el cooldown está activo. El setState va dentro del callback del
    // interval (asíncrono), recomputando siempre desde el `sentAt` persistido (no decrementa a
    // ciegas) → resiste pausas del tab y queda consistente con el reloj real.
    useEffect(() => {
        if (secondsLeft <= 0) return;
        const id = setInterval(() => {
            setSecondsLeft(computeSecondsLeft(readSentAt(flowKey), cooldownSeconds, Date.now()));
        }, 1000);
        return () => {
            clearInterval(id);
        };
    }, [secondsLeft, flowKey, cooldownSeconds]);

    const markSent = useCallback(() => {
        const now = Date.now();
        try {
            sessionStorage.setItem(storageKey(flowKey), String(now));
        } catch {
            /* best-effort: si no podemos persistir, el cooldown queda solo en memoria */
        }
        setSecondsLeft(computeSecondsLeft(now, cooldownSeconds, now));
    }, [flowKey, cooldownSeconds]);

    const clear = useCallback(() => {
        clearOtpCooldown(flowKey);
        setSecondsLeft(0);
    }, [flowKey]);

    return { secondsLeft, canResend: secondsLeft === 0, markSent, clear };
}
